/**
 * Analytics — Panel de analítica con filtros globales, KPIs con sparkline y comparativa,
 * organizado por audiencia (Operativo / Clínico / Financiero / Chatbot).
 *
 * Sub-paso 1: estructura nueva con datos del endpoint /analytics/dashboard.
 * Tabs Clínico y Financiero quedan con placeholder hasta que se agreguen endpoints.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Tabs, Tab, IconButton, Tooltip, Skeleton,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem,
  Chip, Button, LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Insights as InsightsIcon,
  EventAvailable as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  AttachMoney as MoneyIcon,
  LocalHospital as HospitalIcon,
  Lightbulb as LightbulbIcon,
  WarningAmber as WarningIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as AutoAwesomeIcon,
  PersonAdd as PersonAddIcon,
  AccessTime as ClockIcon,
  SyncAlt as ConversionIcon,
  Tag as TagIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, Cell,
  PieChart, Pie,
} from 'recharts';
import { auth } from '../firebase';

const API_URL = '/api';

const PERIODOS = [
  { id: '7d', label: '7 días' },
  { id: '30d', label: '30 días' },
  { id: '90d', label: '90 días' },
  { id: 'all', label: '12 meses' },
];

const TABS = [
  { id: 'operativo',  label: 'Operativo',  icon: <CalendarIcon /> },
  { id: 'clinico',    label: 'Clínico',    icon: <HospitalIcon /> },
  { id: 'financiero', label: 'Financiero', icon: <MoneyIcon /> },
  { id: 'chatbot',    label: 'Chatbot',    icon: <WhatsAppIcon /> },
];

// Paleta semántica
const C = {
  primary:  '#1e3a5f',
  azul:     '#0ea5e9',
  morado:   '#8b5cf6',
  verde:    '#15803d',
  rojo:     '#dc2626',
  ambar:    '#d97706',
  rosa:     '#ec4899',
  slate:    '#64748b',
};

function fmtNum(n) {
  if (n == null) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// ============================================================================
// HEATMAP — matriz día × hora con celdas coloreadas por intensidad
// ============================================================================
function HeatmapDiaHora({ matriz, max, total, fuente, hideEmptyHours = true }) {
  const horaInicio = hideEmptyHours ? 7 : 0;
  const horaFin = hideEmptyHours ? 21 : 23;
  const horas = [];
  for (let h = horaInicio; h <= horaFin; h++) horas.push(h);

  const colorScale = (val) => {
    if (val === 0) return { bg: '#f8fafc', txt: '#cbd5e1' };
    if (max === 0) return { bg: '#f8fafc', txt: '#cbd5e1' };
    const intensidad = val / max;
    if (intensidad < 0.2) return { bg: '#dbeafe', txt: '#1e40af' };
    if (intensidad < 0.4) return { bg: '#93c5fd', txt: '#1e3a8a' };
    if (intensidad < 0.6) return { bg: '#3b82f6', txt: '#fff' };
    if (intensidad < 0.8) return { bg: '#1e3a5f', txt: '#fff' };
    return { bg: '#0f172a', txt: '#fbbf24' };
  };

  if (total === 0) {
    return <EmptyChart label={`Sin ${fuente === 'citas' ? 'citas' : 'conversaciones'} en este período`} />;
  }

  // Formato hora con am/pm para que no se confunda con fechas
  const fmtHora = (h) => {
    if (h === 0) return '12am';
    if (h < 12) return `${h}am`;
    if (h === 12) return '12pm';
    return `${h - 12}pm`;
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
        {/* Etiqueta del eje horizontal */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `42px 1fr`,
          mb: 0.6,
        }}>
          <Box />
          <Typography sx={{
            fontSize: 10, fontWeight: 700, color: '#94a3b8',
            textTransform: 'uppercase', letterSpacing: 0.6, textAlign: 'center',
          }}>
            ← Hora del día →
          </Typography>
        </Box>

        {/* Header de horas */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `42px repeat(${horas.length}, minmax(34px, 1fr))`,
          gap: 0.3,
          mb: 0.4,
        }}>
          <Box />
          {horas.map(h => (
            <Box key={h} sx={{
              fontSize: 9.5, fontWeight: 700, color: '#94a3b8',
              textAlign: 'center', letterSpacing: 0.2,
              whiteSpace: 'nowrap',
            }}>
              {fmtHora(h)}
            </Box>
          ))}
        </Box>

        {/* Filas por día */}
        {DIAS_SEMANA.map((dia, diaIdx) => (
          <Box key={dia} sx={{
            display: 'grid',
            gridTemplateColumns: `42px repeat(${horas.length}, minmax(34px, 1fr))`,
            gap: 0.3,
            mb: 0.3,
          }}>
            <Box sx={{
              fontSize: 11, fontWeight: 700, color: '#475569',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
              pr: 0.5,
            }}>
              {dia}
            </Box>
            {horas.map(h => {
              const val = matriz[diaIdx]?.[h] || 0;
              const { bg, txt } = colorScale(val);
              return (
                <Tooltip key={h} title={`${dia} ${h}:00 — ${val} ${fuente === 'citas' ? 'cita(s)' : 'conv.'}`} arrow placement="top">
                  <Box sx={{
                    aspectRatio: '1.1 / 1',
                    background: bg, color: txt,
                    borderRadius: 0.8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700,
                    cursor: val > 0 ? 'help' : 'default',
                    transition: 'transform 0.12s',
                    '&:hover': val > 0 ? { transform: 'scale(1.15)', zIndex: 1, position: 'relative' } : {},
                  }}>
                    {val > 0 ? val : ''}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}

        {/* Leyenda */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, fontSize: 10.5, color: '#64748b' }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>Menos</Typography>
          {[0.05, 0.3, 0.5, 0.7, 0.95].map(intensidad => {
            const { bg } = colorScale(intensidad * max);
            return <Box key={intensidad} sx={{ width: 14, height: 14, borderRadius: 0.5, background: bg }} />;
          })}
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>Más</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 10.5, color: '#475569' }}>
            Total: <b>{total}</b> {fuente === 'citas' ? 'citas' : 'conversaciones'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// COHORT RETENTION — matriz triangular de retención mes a mes
// ============================================================================
function CohortRetention({ data }) {
  if (!data || !data.cohorts || data.cohorts.length === 0) {
    return <EmptyChart label="Sin pacientes en el rango analizado" />;
  }

  // Determinar el ancho máximo de la tabla (mayor número de offsets en cualquier cohort)
  const maxOffsets = Math.max(...data.cohorts.map(c => c.retencion.length));

  // Color scale 0-100% → de gris claro a azul oscuro+dorado
  const colorScale = (pct) => {
    if (pct === 0) return { bg: '#f8fafc', txt: '#cbd5e1' };
    if (pct < 10)  return { bg: '#dbeafe', txt: '#1e40af' };
    if (pct < 25)  return { bg: '#93c5fd', txt: '#1e3a8a' };
    if (pct < 50)  return { bg: '#3b82f6', txt: '#fff' };
    if (pct < 75)  return { bg: '#1e3a5f', txt: '#fff' };
    return { bg: '#0f172a', txt: '#fbbf24' };
  };

  const fmtMes = (s) => {
    const [y, m] = s.split('-').map(Number);
    const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
    return `${meses[m-1]} ${String(y).slice(-2)}`;
  };

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
        {/* Header: cohort + offset labels */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `120px 60px repeat(${maxOffsets}, minmax(56px, 1fr))`,
          gap: 0.4, mb: 0.6,
        }}>
          <Box sx={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Cohort
          </Box>
          <Box sx={{ fontSize: 10.5, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'center' }}>
            Tamaño
          </Box>
          {Array.from({ length: maxOffsets }, (_, i) => (
            <Box key={i} sx={{
              fontSize: 10.5, fontWeight: 700, color: '#475569',
              textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.4,
            }}>
              M+{i}
            </Box>
          ))}
        </Box>

        {/* Filas — una por cohort */}
        {data.cohorts.map(c => (
          <Box key={c.mes} sx={{
            display: 'grid',
            gridTemplateColumns: `120px 60px repeat(${maxOffsets}, minmax(56px, 1fr))`,
            gap: 0.4, mb: 0.4, alignItems: 'stretch',
          }}>
            <Box sx={{
              fontSize: 12, fontWeight: 700, color: '#1e3a5f',
              display: 'flex', alignItems: 'center',
            }}>
              {fmtMes(c.mes)}
            </Box>
            <Box sx={{
              fontSize: 12, fontWeight: 700, color: '#475569',
              textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#f1f5f9', borderRadius: 1,
            }}>
              {c.tamano}
            </Box>
            {Array.from({ length: maxOffsets }, (_, i) => {
              const punto = c.retencion[i];
              if (!punto) {
                return <Box key={i} sx={{ background: 'transparent' }} />;
              }
              const { bg, txt } = colorScale(punto.pct);
              return (
                <Tooltip
                  key={i}
                  title={`${fmtMes(c.mes)} → M+${i}: ${punto.cantidad} de ${c.tamano} pacientes regresaron (${punto.pct}%)`}
                  arrow placement="top"
                >
                  <Box sx={{
                    minHeight: 32,
                    background: bg, color: txt,
                    borderRadius: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 800,
                    cursor: 'help',
                    transition: 'transform 0.12s',
                    '&:hover': { transform: 'scale(1.08)', zIndex: 1, position: 'relative' },
                  }}>
                    {punto.pct > 0 ? `${punto.pct}%` : '—'}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        ))}

        {/* Fila promedio */}
        {data.promedio_por_offset && data.promedio_por_offset.length > 0 && (
          <>
            <Box sx={{ height: 8 }} />
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: `120px 60px repeat(${maxOffsets}, minmax(56px, 1fr))`,
              gap: 0.4,
              borderTop: '2px solid #e2e8f0',
              pt: 0.8,
            }}>
              <Box sx={{
                fontSize: 11, fontWeight: 800, color: '#1e3a5f',
                textTransform: 'uppercase', letterSpacing: 0.4,
                display: 'flex', alignItems: 'center',
              }}>
                Promedio
              </Box>
              <Box />
              {data.promedio_por_offset.map((p, i) => {
                const { bg, txt } = colorScale(p.pct);
                return (
                  <Tooltip key={i} title={`Promedio M+${i}: ${p.pct}% (${p.cohorts_incluidas} cohorts)`} arrow placement="top">
                    <Box sx={{
                      minHeight: 30,
                      background: bg, color: txt,
                      borderRadius: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800,
                      border: '2px solid #fbbf24',
                    }}>
                      {p.pct}%
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </>
        )}

        {/* Leyenda */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, fontSize: 10.5, color: '#64748b' }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>0%</Typography>
          {[5, 20, 40, 60, 85].map(pct => {
            const { bg } = colorScale(pct);
            return <Box key={pct} sx={{ width: 18, height: 14, borderRadius: 0.5, background: bg }} />;
          })}
          <Typography sx={{ fontSize: 10.5, fontWeight: 600 }}>100%</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 10.5, color: '#475569' }}>
            <b>{data.total_pacientes}</b> pacientes en {data.cohorts.length} cohort{data.cohorts.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// FUNNEL — embudo de conversión con tasas paso a paso
// ============================================================================
function FunnelChart({ pasos }) {
  if (!pasos || pasos.length === 0 || pasos[0].valor === 0) {
    return <EmptyChart label="Sin datos para el funnel" />;
  }
  const max = pasos[0].valor;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {pasos.map((p, i) => {
        const ancho = max > 0 ? (p.valor / max) * 100 : 0;
        const dropoff = i > 0 ? pasos[i - 1].valor - p.valor : 0;
        return (
          <Box key={p.id}>
            {/* Drop-off entre pasos */}
            {i > 0 && dropoff > 0 && (
              <Box sx={{
                ml: 2, mb: 0.6,
                display: 'flex', alignItems: 'center', gap: 0.5,
                fontSize: 10.5, color: '#94a3b8',
              }}>
                <TrendingDownIcon sx={{ fontSize: 12 }} />
                <Typography sx={{ fontSize: 10.5 }}>
                  − {dropoff} pierden ({(100 - p.pct_paso_anterior).toFixed(1)}%)
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
              {/* Barra del paso — gradiente navy con acento en borde izquierdo (color semántico del paso) */}
              <Box sx={{
                position: 'relative',
                width: `${Math.max(ancho, 12)}%`, minWidth: 200,
                height: 56,
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                borderLeft: `4px solid ${p.color}`,
                borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, color: '#fff',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                transition: 'all 0.3s',
              }}>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {p.label}
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1 }}>
                    {fmtNum(p.valor)}
                  </Typography>
                </Box>
                {i > 0 && p.pct_paso_anterior < 100 && (
                  <Box sx={{
                    background: 'rgba(255,255,255,0.18)',
                    px: 1, py: 0.4, borderRadius: 1,
                    fontSize: 12, fontWeight: 800,
                  }}>
                    {p.pct_paso_anterior}%
                  </Box>
                )}
              </Box>
              {/* % del total a la derecha */}
              <Box sx={{
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                minWidth: 80,
              }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Del total
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: p.color, lineHeight: 1 }}>
                  {p.pct_total}%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

// ============================================================================
export default function Analytics() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [tab, setTab] = useState(() => localStorage.getItem('analytics_tab') || 'operativo');
  const [periodo, setPeriodo] = useState('30d');
  const [chatbotFilter, setChatbotFilter] = useState('');
  const [empresaFilter, setEmpresaFilter] = useState('');
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) { navigate('/login'); return; }
      const stored = localStorage.getItem('user_id');
      if (stored) {
        setUserId(stored);
      } else {
        try {
          const r = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
          const d = await r.json();
          if (d?.id) {
            localStorage.setItem('user_id', String(d.id));
            setUserId(String(d.id));
          }
        } catch (e) { console.error(e); }
      }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => { localStorage.setItem('analytics_tab', tab); }, [tab]);

  const cargar = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ periodo });
      if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
      if (empresaFilter) params.append('empresa_id', empresaFilter);

      const res = await fetch(`${API_URL}/analytics/dashboard?${params}`, {
        headers: { 'X-User-Id': userId },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDatos(data);
    } catch (e) {
      console.error(e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* HEADER */}
      <Box sx={{
        px: { xs: 2, sm: 4, md: 5 }, py: 2.5,
        background: '#fff', borderBottom: '1px solid #e2e8f0',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: { xs: 18, sm: 22 }, fontWeight: 700, color: '#1e293b', mb: 0.2 }}>
              Analítica
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Métricas operativas, clínicas, financieras y del chatbot
            </Typography>
          </Box>

          {/* Filtros globales */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={periodo} exclusive size="small"
              onChange={(_, v) => v && setPeriodo(v)}
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none', fontSize: 12, fontWeight: 700, px: 1.5, py: 0.5,
                  border: '1px solid #e2e8f0', color: '#475569',
                  '&.Mui-selected': {
                    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                    color: '#fff', borderColor: 'transparent',
                    '&:hover': { background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)' },
                  },
                },
              }}
            >
              {PERIODOS.map(p => (
                <ToggleButton key={p.id} value={p.id}>{p.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>

            {datos?.chatbots && datos.chatbots.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel shrink>Chatbot</InputLabel>
                <Select
                  label="Chatbot" notched displayEmpty
                  value={chatbotFilter}
                  onChange={(e) => setChatbotFilter(e.target.value)}
                  sx={{ fontSize: 13, borderRadius: 2, background: '#f8fafc' }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {datos.chatbots.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {datos?.es_superadmin && datos?.empresas && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel shrink>Empresa</InputLabel>
                <Select
                  label="Empresa" notched displayEmpty
                  value={empresaFilter}
                  onChange={(e) => setEmpresaFilter(e.target.value)}
                  sx={{ fontSize: 13, borderRadius: 2, background: '#f8fafc' }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {datos.empresas.map(e => (
                    <MenuItem key={e.id} value={e.id}>{e.nombre || e.company}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Tooltip title="Recargar">
              <IconButton size="small" onClick={cargar} sx={{ color: '#64748b' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* TABS */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 5 }, background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Tabs
            value={tab} onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#64748b',
                '&.Mui-selected': { color: '#1e3a5f' },
              },
              '& .MuiTabs-indicator': { background: '#1e3a5f', height: 2.5 },
            }}
          >
            {TABS.map(t => (
              <Tab key={t.id} value={t.id}
                icon={React.cloneElement(t.icon, { sx: { fontSize: 16 } })}
                iconPosition="start" label={t.label}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box sx={{ px: { xs: 2, sm: 4, md: 5 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
        {loading && <LoadingState />}
        {error && !loading && <ErrorState mensaje={error} onRetry={cargar} />}
        {!loading && !error && datos && (
          <>
            {tab === 'operativo' && <OperativoTab datos={datos} periodo={periodo} userId={userId} chatbotFilter={chatbotFilter} empresaFilter={empresaFilter} />}
            {tab === 'clinico' && <ClinicoTab userId={userId} periodo={periodo} chatbotFilter={chatbotFilter} empresaFilter={empresaFilter} />}
            {tab === 'financiero' && <FinancieroTab userId={userId} periodo={periodo} chatbotFilter={chatbotFilter} empresaFilter={empresaFilter} />}
            {tab === 'chatbot' && <ChatbotTab datos={datos} userId={userId} periodo={periodo} chatbotFilter={chatbotFilter} empresaFilter={empresaFilter} />}
          </>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// TAB: OPERATIVO
// ============================================================================
function OperativoTab({ datos, periodo, userId, chatbotFilter, empresaFilter }) {
  const r = datos.resumen || {};
  const cmp = datos.comparacion || {};
  const sparklineConv = datos.sparkline_conv || [];
  const [heatmap, setHeatmap] = useState(null);
  const [briefingIA, setBriefingIA] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Construir datos de sparkline a partir de citas_por_dia
  const sparkCitas = useMemo(() => {
    return (datos.citas_por_dia || []).map(d => ({
      fecha: d.fecha,
      total: (d.confirmadas || 0) + (d.canceladas || 0) + (d.pendientes || 0),
    }));
  }, [datos.citas_por_dia]);

  // Cargar heatmap
  useEffect(() => {
    if (!userId) return;
    const params = new URLSearchParams({ periodo, fuente: 'citas' });
    if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
    if (empresaFilter) params.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/heatmap?${params}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : null)
      .then(setHeatmap)
      .catch(console.error);
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  // Cargar Briefing IA
  const cargarBriefing = useCallback(async (forzar = false) => {
    if (!userId) return;
    setBriefingLoading(true);
    try {
      const params = new URLSearchParams({ periodo });
      if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
      if (empresaFilter) params.append('empresa_id', empresaFilter);
      if (forzar) params.append('forzar', 'true');
      const res = await fetch(`${API_URL}/analytics/briefing-ia?${params}`, { headers: { 'X-User-Id': userId } });
      if (res.ok) setBriefingIA(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setBriefingLoading(false);
    }
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  useEffect(() => { cargarBriefing(false); }, [cargarBriefing]);

  // Cargar Smart Goals + Forecast (citas e ingresos)
  const [smartGoals, setSmartGoals] = useState(null);
  const [forecastCitas, setForecastCitas] = useState(null);
  const [forecastIngresos, setForecastIngresos] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const headers = { 'X-User-Id': userId };
    const baseParams = new URLSearchParams();
    if (chatbotFilter) baseParams.append('chatbot_id', chatbotFilter);
    if (empresaFilter) baseParams.append('empresa_id', empresaFilter);

    fetch(`${API_URL}/analytics/smart-goals?${baseParams}`, { headers })
      .then(r => r.ok ? r.json() : null).then(setSmartGoals).catch(console.error);

    const fcParams = (m) => {
      const p = new URLSearchParams({ metrica: m, dias_historico: '90', dias_proyeccion: '30' });
      if (chatbotFilter) p.append('chatbot_id', chatbotFilter);
      if (empresaFilter) p.append('empresa_id', empresaFilter);
      return p;
    };
    fetch(`${API_URL}/analytics/forecast?${fcParams('citas')}`, { headers })
      .then(r => r.ok ? r.json() : null).then(setForecastCitas).catch(console.error);
    fetch(`${API_URL}/analytics/forecast?${fcParams('ingresos')}`, { headers })
      .then(r => r.ok ? r.json() : null).then(setForecastIngresos).catch(console.error);
  }, [userId, chatbotFilter, empresaFilter]);

  return (
    <Box>
      {/* Briefing IA — banner destacado al inicio */}
      <BriefingIABanner
        data={briefingIA}
        loading={briefingLoading}
        onRegenerar={() => cargarBriefing(true)}
      />

      {/* Anomalías detectadas */}
      {briefingIA?.anomalias && briefingIA.anomalias.length > 0 && (
        <AnomaliasBanner anomalias={briefingIA.anomalias} />
      )}

      {/* Smart Goals */}
      {smartGoals && smartGoals.goals && smartGoals.goals.length > 0 && (
        <SmartGoalsPanel goals={smartGoals.goals} />
      )}

      {/* Insights destacados (legacy, del backend dashboard) */}
      {datos.insights && datos.insights.length > 0 && (
        <InsightsBar insights={datos.insights} />
      )}

      {/* KPI Cards row 1: principales */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard
          icon={<CalendarIcon />}
          label="Citas agendadas"
          valor={r.total_citas || 0}
          subtitulo={`${r.citas_confirmadas || 0} confirmadas`}
          cambio={cmp.cambio_citas}
          color={C.azul}
          sparkline={sparkCitas}
          sparkKey="total"
        />
        <KpiCard
          icon={<PersonAddIcon />}
          label="Pacientes nuevos"
          valor={r.clientes_nuevos || r.total_clientes || 0}
          subtitulo={`${r.clientes_con_cita || 0} agendaron`}
          cambio={cmp.cambio_clientes}
          color={C.verde}
        />
        <KpiCard
          icon={<ConversionIcon />}
          label="Tasa de conversión"
          valor={`${r.tasa_conversion || 0}%`}
          subtitulo="conversaciones → cita"
          color={C.morado}
          highlight
        />
        <KpiCard
          icon={<ClockIcon />}
          label="Horas ahorradas"
          valor={`${r.horas_ahorradas || 0}h`}
          subtitulo={`${r.total_conversaciones || 0} chats atendidos`}
          cambio={cmp.cambio_conversaciones}
          color={C.ambar}
          sparkline={sparklineConv.map((v, i) => ({ fecha: i, total: v }))}
          sparkKey="total"
        />
      </Box>

      {/* Hoy / Mañana / Pendientes */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
        <MiniStat label="Citas hoy" valor={r.citas_hoy || 0} color={C.azul} />
        <MiniStat label="Citas mañana" valor={r.citas_manana || 0} color={C.morado} />
        <MiniStat label="Pendientes" valor={r.citas_pendientes || 0} color={C.ambar} />
        <MiniStat label="Recordatorios enviados" valor={(r.recordatorios_24h || 0) + (r.recordatorios_12h || 0)} color={C.verde} />
      </Box>

      {/* Charts grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' }, gap: 2, mb: 2 }}>
        <ChartPanel title="Citas por día" subtitulo={`Últimos ${PERIODOS.find(p => p.id === periodo)?.label || ''}`}>
          {(datos.citas_por_dia || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datos.citas_por_dia}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <RTooltip contentStyle={chartTooltip} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Bar dataKey="confirmadas" stackId="a" fill={C.verde} name="Confirmadas" radius={[0,0,0,0]} />
                <Bar dataKey="pendientes" stackId="a" fill={C.ambar} name="Pendientes" />
                <Bar dataKey="canceladas" stackId="a" fill={C.rojo} name="Canceladas" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin citas en este período" />
          )}
        </ChartPanel>

        <ChartPanel title="Distribución horaria" subtitulo="Cuándo se reciben más conversaciones">
          {(datos.distribucion_horaria || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datos.distribucion_horaria}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="hora" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false}
                  tickFormatter={(h) => `${h}h`} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <RTooltip contentStyle={chartTooltip} formatter={(v) => [v, 'Conversaciones']} labelFormatter={(h) => `${h}:00`} />
                <Bar dataKey="conversaciones" fill={C.azul} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin actividad" />
          )}
        </ChartPanel>
      </Box>

      {/* Heatmap día × hora — full width */}
      <ChartPanel
        title="Heatmap de citas (día × hora)"
        subtitulo="Identifica los slots más demandados de tu agenda"
      >
        {heatmap
          ? <HeatmapDiaHora matriz={heatmap.matriz} max={heatmap.max} total={heatmap.total} fuente="citas" />
          : <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />}
      </ChartPanel>

      {/* Forecast — proyecciones próximos 30 días */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
        <ChartPanel title="Proyección de citas (30 días)" subtitulo="Tendencia basada en últimos 90 días">
          {forecastCitas
            ? <ForecastChart data={forecastCitas} color={C.azul} unidad="" />
            : <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />}
        </ChartPanel>
        <ChartPanel title="Proyección de ingresos (30 días)" subtitulo="Tendencia basada en últimos 90 días">
          {forecastIngresos
            ? <ForecastChart data={forecastIngresos} color={C.verde} unidad="$" />
            : <Skeleton variant="rounded" height={260} sx={{ borderRadius: 2 }} />}
        </ChartPanel>
      </Box>
    </Box>
  );
}

// ============================================================================
// TAB: CHATBOT
// ============================================================================
function ChatbotTab({ datos, userId, periodo, chatbotFilter, empresaFilter }) {
  const r = datos.resumen || {};
  const cmp = datos.comparacion || {};
  const totalTokens = (datos.chatbots || []).reduce((s, c) => s + (c.tokens_usados || 0), 0);
  const costoEstimado = totalTokens * 0.000005; // estimación gpt-4o-mini precio promedio
  const [funnel, setFunnel] = useState(null);
  const [heatmapConv, setHeatmapConv] = useState(null);

  useEffect(() => {
    if (!userId) return;
    const params = new URLSearchParams({ periodo });
    if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
    if (empresaFilter) params.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/funnel?${params}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : null)
      .then(setFunnel)
      .catch(console.error);

    const params2 = new URLSearchParams({ periodo, fuente: 'conversaciones' });
    if (chatbotFilter) params2.append('chatbot_id', chatbotFilter);
    if (empresaFilter) params2.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/heatmap?${params2}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : null)
      .then(setHeatmapConv)
      .catch(console.error);
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  return (
    <Box>
      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard
          icon={<WhatsAppIcon />}
          label="Conversaciones"
          valor={r.total_conversaciones || 0}
          cambio={cmp.cambio_conversaciones}
          color="#25D366"
        />
        <KpiCard
          icon={<PersonAddIcon />}
          label="Clientes únicos"
          valor={r.total_clientes || 0}
          cambio={cmp.cambio_clientes}
          color={C.azul}
        />
        <KpiCard
          icon={<AutoAwesomeIcon />}
          label="Tokens consumidos"
          valor={fmtNum(totalTokens)}
          subtitulo={`~$${costoEstimado.toFixed(2)} USD`}
          color={C.morado}
        />
        <KpiCard
          icon={<ConversionIcon />}
          label="Tasa de conversión"
          valor={`${r.tasa_conversion || 0}%`}
          subtitulo="chat → cita"
          color={C.verde}
          highlight
        />
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
        <ChartPanel title="Conversaciones por día" subtitulo="Por resultado">
          {(datos.conversaciones_por_dia || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={datos.conversaciones_por_dia}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <RTooltip contentStyle={chartTooltip} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                <Area type="monotone" dataKey="completadas" stackId="1" stroke={C.verde} fill={C.verde} fillOpacity={0.7} name="Completadas" />
                <Area type="monotone" dataKey="escaladas" stackId="1" stroke={C.ambar} fill={C.ambar} fillOpacity={0.7} name="Escaladas" />
                <Area type="monotone" dataKey="timeout" stackId="1" stroke={C.slate} fill={C.slate} fillOpacity={0.5} name="Timeout" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin conversaciones" />
          )}
        </ChartPanel>

        <ChartPanel title="Temas más frecuentes" subtitulo="Top 10">
          {(datos.temas_frecuentes || []).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, maxHeight: 260, overflowY: 'auto', pr: 1 }}>
              {datos.temas_frecuentes.map((t, i) => {
                const max = datos.temas_frecuentes[0]?.cantidad || 1;
                const pct = (t.cantidad / max) * 100;
                return (
                  <Box key={i} sx={{ position: 'relative', p: 1, borderRadius: 1.5, background: '#f8fafc' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: '#dbeafe', borderRadius: 1.5, opacity: 0.6 }} />
                    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1e293b' }}>
                        {t.tema}
                      </Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.azul }}>
                        {t.cantidad}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <EmptyChart label="Sin temas detectados" />
          )}
        </ChartPanel>
      </Box>

      {/* Funnel de conversión + Heatmap de conversaciones */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <ChartPanel title="Funnel de conversión" subtitulo="De conversación a consulta pagada">
          {funnel
            ? <FunnelChart pasos={funnel.pasos} />
            : <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />}
        </ChartPanel>

        <ChartPanel title="Heatmap de conversaciones" subtitulo="Cuándo escriben los pacientes">
          {heatmapConv
            ? <HeatmapDiaHora matriz={heatmapConv.matriz} max={heatmapConv.max} total={heatmapConv.total} fuente="conversaciones" />
            : <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />}
        </ChartPanel>
      </Box>

      {/* Tabla de chatbots */}
      {(datos.chatbots || []).length > 0 && (
        <ChartPanel title="Rendimiento por chatbot" subtitulo={`${datos.chatbots.length} chatbots activos`}>
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: '2px solid #e2e8f0' }}>
                  {['Chatbot', 'Propietario', 'Conversaciones', 'Clientes', 'Citas', 'Tasa conv.', 'Tokens'].map(h => (
                    <Box component="th" key={h} sx={{
                      textAlign: h === 'Chatbot' || h === 'Propietario' ? 'left' : 'right',
                      p: 1.2, fontSize: 11, fontWeight: 700, color: '#475569',
                      textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {datos.chatbots.map((c, i) => (
                  <Box component="tr" key={c.id} sx={{
                    borderBottom: i === datos.chatbots.length - 1 ? 'none' : '1px solid #f1f5f9',
                    '&:hover': { background: '#fafbfc' },
                  }}>
                    <Box component="td" sx={{ p: 1.2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BotIcon sx={{ fontSize: 16, color: C.morado }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{c.nombre}</Typography>
                      </Box>
                    </Box>
                    <Box component="td" sx={{ p: 1.2, color: '#64748b' }}>{c.propietario}</Box>
                    <Box component="td" sx={{ p: 1.2, textAlign: 'right', fontWeight: 600 }}>{c.conversaciones}</Box>
                    <Box component="td" sx={{ p: 1.2, textAlign: 'right', fontWeight: 600 }}>{c.clientes}</Box>
                    <Box component="td" sx={{ p: 1.2, textAlign: 'right', fontWeight: 700, color: C.verde }}>{c.citas}</Box>
                    <Box component="td" sx={{ p: 1.2, textAlign: 'right' }}>
                      <Chip label={`${c.tasa_conversion}%`} size="small"
                        sx={{
                          height: 20, fontSize: 11, fontWeight: 700,
                          background: c.tasa_conversion >= 30 ? '#dcfce7' : c.tasa_conversion >= 15 ? '#fef3c7' : '#fee2e2',
                          color: c.tasa_conversion >= 30 ? '#15803d' : c.tasa_conversion >= 15 ? '#b45309' : '#991b1b',
                        }}
                      />
                    </Box>
                    <Box component="td" sx={{ p: 1.2, textAlign: 'right', color: '#64748b', fontFamily: 'monospace', fontSize: 11.5 }}>
                      {fmtNum(c.tokens_usados)}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </ChartPanel>
      )}
    </Box>
  );
}

// ============================================================================
// TAB: CLÍNICO
// ============================================================================
function ClinicoTab({ userId, periodo, chatbotFilter, empresaFilter }) {
  const [datos, setDatos] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const params = new URLSearchParams({ periodo });
    if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
    if (empresaFilter) params.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/clinico?${params}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setDatos)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));

    // Cohort retention: 6 meses (independiente del filtro de período)
    const params2 = new URLSearchParams({ meses_atras: '6' });
    if (empresaFilter) params2.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/cohort?${params2}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : null)
      .then(setCohort)
      .catch(console.error);
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState mensaje={error} onRetry={() => setDatos(null)} />;
  if (!datos) return null;

  const TIPO_LABEL = {
    primera_vez: 'Primera vez',
    subsecuente: 'Subsecuente',
    urgencia: 'Urgencia',
    interconsulta: 'Interconsulta',
    valoracion: 'Valoración',
  };
  const TIPO_COLORS = ['#1e3a5f', '#0ea5e9', '#dc2626', '#7c3aed', '#15803d'];

  const SEXO_LABEL = { masculino: 'Masculino', femenino: 'Femenino', otro: 'Otro' };
  const SEXO_COLORS = { masculino: '#0ea5e9', femenino: '#ec4899', otro: '#94a3b8' };

  return (
    <Box>
      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard
          icon={<HospitalIcon />}
          label="Consultas atendidas"
          valor={datos.total_consultas || 0}
          cambio={datos.comparacion?.cambio_consultas}
          color={C.primary}
        />
        <KpiCard
          icon={<PersonAddIcon />}
          label="Pacientes en BD"
          valor={datos.total_pacientes || 0}
          color={C.azul}
        />
        <KpiCard
          icon={<ClockIcon />}
          label="Duración promedio"
          valor={`${datos.duracion_promedio_min || 0}m`}
          subtitulo="por consulta"
          color={C.morado}
        />
        <KpiCard
          icon={<TagIcon />}
          label="Diagnósticos únicos"
          valor={(datos.diagnosticos_top || []).length}
          subtitulo="top 10 mostrados"
          color={C.ambar}
        />
      </Box>

      {/* Charts grid: diagnósticos + demografía */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2, mb: 2 }}>
        <ChartPanel title="Top 10 diagnósticos" subtitulo="Códigos CIE-10 más frecuentes">
          {(datos.diagnosticos_top || []).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, maxHeight: 320, overflowY: 'auto', pr: 1 }}>
              {datos.diagnosticos_top.map((d, i) => {
                const max = datos.diagnosticos_top[0]?.cantidad || 1;
                const pct = (d.cantidad / max) * 100;
                return (
                  <Box key={i} sx={{ position: 'relative', p: 1, borderRadius: 1.5, background: '#f8fafc', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: '#dbeafe', opacity: 0.6 }} />
                    <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Chip label={d.codigo} size="small" sx={{
                          height: 20, fontSize: 10.5, fontWeight: 700, fontFamily: 'monospace',
                          background: '#1e3a5f', color: '#fff',
                        }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.descripcion}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: C.primary, flexShrink: 0 }}>
                        {d.cantidad}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <EmptyChart label="Sin diagnósticos en este período" />
          )}
        </ChartPanel>

        <ChartPanel title="Distribución por sexo" subtitulo={`${datos.total_pacientes || 0} pacientes`}>
          {(datos.por_sexo || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={datos.por_sexo} dataKey="cantidad" nameKey="sexo"
                  cx="50%" cy="50%" outerRadius={80} innerRadius={50}
                  label={(e) => `${SEXO_LABEL[e.sexo] || e.sexo}: ${e.cantidad}`}
                >
                  {datos.por_sexo.map((entry, i) => (
                    <Cell key={i} fill={SEXO_COLORS[entry.sexo] || '#94a3b8'} />
                  ))}
                </Pie>
                <RTooltip contentStyle={chartTooltip} formatter={(v, k, p) => [v, SEXO_LABEL[p.payload.sexo] || p.payload.sexo]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin datos demográficos" />
          )}
        </ChartPanel>
      </Box>

      {/* Charts grid: edad + tipo consulta */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, gap: 2 }}>
        <ChartPanel title="Distribución por edad" subtitulo="Pacientes registrados">
          {(datos.por_edad || []).some(e => e.cantidad > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datos.por_edad}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="rango" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <RTooltip contentStyle={chartTooltip} formatter={(v) => [v, 'Pacientes']} />
                <Bar dataKey="cantidad" fill={C.azul} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin datos de edad" />
          )}
        </ChartPanel>

        <ChartPanel title="Consultas por tipo" subtitulo={`Últimos ${PERIODOS.find(p => p.id === periodo)?.label}`}>
          {(datos.consultas_por_tipo || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={datos.consultas_por_tipo} dataKey="cantidad" nameKey="tipo"
                  cx="50%" cy="50%" outerRadius={80} innerRadius={50}
                  label={(e) => `${TIPO_LABEL[e.tipo] || e.tipo}: ${e.cantidad}`}
                >
                  {datos.consultas_por_tipo.map((_, i) => (
                    <Cell key={i} fill={TIPO_COLORS[i % TIPO_COLORS.length]} />
                  ))}
                </Pie>
                <RTooltip contentStyle={chartTooltip} formatter={(v, k, p) => [v, TIPO_LABEL[p.payload.tipo] || p.payload.tipo]} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin consultas" />
          )}
        </ChartPanel>
      </Box>

      {/* Cohort retention — full width al final */}
      <Box sx={{ mt: 2 }}>
        <ChartPanel
          title="Retención de pacientes (cohort)"
          subtitulo="¿Qué % de los pacientes registrados cada mes regresaron en los meses siguientes?"
        >
          {cohort
            ? <CohortRetention data={cohort} />
            : <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />}
        </ChartPanel>
      </Box>
    </Box>
  );
}

// ============================================================================
// TAB: FINANCIERO
// ============================================================================
function FinancieroTab({ userId, periodo, chatbotFilter, empresaFilter }) {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const params = new URLSearchParams({ periodo });
    if (chatbotFilter) params.append('chatbot_id', chatbotFilter);
    if (empresaFilter) params.append('empresa_id', empresaFilter);
    fetch(`${API_URL}/analytics/financiero?${params}`, { headers: { 'X-User-Id': userId } })
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(setDatos)
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [userId, periodo, chatbotFilter, empresaFilter]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState mensaje={error} onRetry={() => setDatos(null)} />;
  if (!datos) return null;

  const fmtMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(Number(n || 0));

  const METODO_LABEL = {
    efectivo: 'Efectivo', tarjeta_debito: 'Débito', tarjeta_credito: 'Crédito',
    transferencia: 'Transferencia', cheque: 'Cheque', mercado_pago: 'Mercado Pago',
    stripe: 'Stripe', otro: 'Otro',
  };
  const METODO_COLORS = ['#15803d', '#0ea5e9', '#7c3aed', '#d97706', '#ec4899', '#0891b2', '#dc2626', '#64748b'];

  const AGING_LABEL = { '0_30': '0-30 días', '31_60': '31-60 días', '61_90': '61-90 días', mas_90: '>90 días' };
  const AGING_COLORS = { '0_30': '#15803d', '31_60': '#d97706', '61_90': '#dc2626', mas_90: '#991b1b' };

  // Datos formateados para gráficos
  const ingresosMes = (datos.ingresos_por_mes || []).map(m => ({
    mes: new Date(m.mes + '-01').toLocaleDateString('es', { month: 'short', year: '2-digit' }),
    total: m.total,
    cantidad: m.cantidad_pagos,
  }));

  const arData = Object.entries(datos.ar_aging || {}).map(([k, v]) => ({
    rango: AGING_LABEL[k] || k,
    monto: v,
    cantidad: datos.ar_count?.[k] || 0,
    color: AGING_COLORS[k],
  }));

  return (
    <Box>
      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard
          icon={<MoneyIcon />}
          label="Ingresos del período"
          valor={fmtMoney(datos.total_periodo)}
          cambio={datos.cambio_pct}
          color={C.verde}
          highlight
        />
        <KpiCard
          icon={<TagIcon />}
          label="Ticket promedio"
          valor={fmtMoney(datos.ticket_promedio)}
          subtitulo="por cargo emitido"
          color={C.azul}
        />
        <KpiCard
          icon={<PersonAddIcon />}
          label="Pacientes facturados"
          valor={datos.pacientes_facturados || 0}
          color={C.morado}
        />
        <KpiCard
          icon={<WarningIcon />}
          label="Saldo por cobrar"
          valor={fmtMoney(datos.ar_total)}
          subtitulo={`${Object.values(datos.ar_count || {}).reduce((a,b) => a+b, 0)} cargos pendientes`}
          color={datos.ar_total > 0 ? C.rojo : C.verde}
        />
      </Box>

      {/* Ingresos por mes (line chart) — full width */}
      <Box sx={{ mb: 2 }}>
        <ChartPanel title="Ingresos por mes" subtitulo="Últimos 12 meses">
          {ingresosMes.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={ingresosMes}>
                <defs>
                  <linearGradient id="gradVerde" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.verde} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={C.verde} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false}
                  tickFormatter={(v) => fmtNum(v)} />
                <RTooltip contentStyle={chartTooltip}
                  formatter={(v) => [fmtMoney(v), 'Ingresos']}
                  labelFormatter={(l) => `Mes: ${l}`}
                />
                <Area type="monotone" dataKey="total" stroke={C.verde} strokeWidth={2.5} fill="url(#gradVerde)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyChart label="Sin ingresos registrados" />
          )}
        </ChartPanel>
      </Box>

      {/* Por método + AR aging */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.2fr' }, gap: 2, mb: 2 }}>
        <ChartPanel title="Ingresos por método de pago" subtitulo={`Últimos ${PERIODOS.find(p => p.id === periodo)?.label}`}>
          {(datos.por_metodo || []).length > 0 ? (
            <Box>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={datos.por_metodo} dataKey="total" nameKey="metodo"
                    cx="50%" cy="50%" outerRadius={70} innerRadius={45}
                  >
                    {datos.por_metodo.map((_, i) => (
                      <Cell key={i} fill={METODO_COLORS[i % METODO_COLORS.length]} />
                    ))}
                  </Pie>
                  <RTooltip contentStyle={chartTooltip}
                    formatter={(v, k, p) => [fmtMoney(v), METODO_LABEL[p.payload.metodo] || p.payload.metodo]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, mt: 1 }}>
                {datos.por_metodo.map((m, i) => (
                  <Box key={m.metodo} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, fontSize: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flex: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: METODO_COLORS[i % METODO_COLORS.length] }} />
                      <Typography sx={{ fontSize: 12, color: '#475569' }}>{METODO_LABEL[m.metodo] || m.metodo}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {fmtMoney(m.total)} <Box component="span" sx={{ color: '#94a3b8', fontSize: 10.5, ml: 0.5 }}>({m.porcentaje}%)</Box>
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : (
            <EmptyChart label="Sin pagos en el período" />
          )}
        </ChartPanel>

        <ChartPanel title="Cuentas por cobrar (AR aging)" subtitulo="Antigüedad de saldos pendientes">
          {(datos.ar_total || 0) > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {arData.map((a) => {
                const pct = datos.ar_total > 0 ? (a.monto / datos.ar_total) * 100 : 0;
                return (
                  <Box key={a.rango}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: a.color }}>
                        {a.rango}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, color: a.color, fontFamily: 'monospace' }}>
                          {fmtMoney(a.monto)}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
                          ({a.cantidad} cargo{a.cantidad !== 1 ? 's' : ''})
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 1, background: '#f1f5f9', overflow: 'hidden' }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, background: a.color, transition: 'width 0.4s' }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <EmptyChart label="Sin cuentas por cobrar" />
          )}
        </ChartPanel>
      </Box>

      {/* Top servicios facturados */}
      <ChartPanel title="Top 10 servicios facturados" subtitulo="Por ingreso total en el período">
        {(datos.top_servicios || []).length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <Box component="thead">
                <Box component="tr" sx={{ borderBottom: '2px solid #e2e8f0' }}>
                  {['#', 'Servicio', 'Cantidad', 'Total facturado'].map((h, i) => (
                    <Box component="th" key={h} sx={{
                      textAlign: i < 2 ? 'left' : 'right',
                      p: 1.2, fontSize: 11, fontWeight: 700, color: '#475569',
                      textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {datos.top_servicios.map((s, i) => {
                  const max = datos.top_servicios[0]?.total || 1;
                  const pct = (s.total / max) * 100;
                  return (
                    <Box component="tr" key={i} sx={{
                      borderBottom: i === datos.top_servicios.length - 1 ? 'none' : '1px solid #f1f5f9',
                      '&:hover': { background: '#fafbfc' },
                    }}>
                      <Box component="td" sx={{ p: 1.2 }}>
                        <Box sx={{
                          width: 24, height: 24, borderRadius: 1, background: '#eff6ff', color: C.primary,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 800,
                        }}>
                          {i + 1}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ p: 1.2, position: 'relative' }}>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{s.servicio}</Typography>
                          <Box sx={{ height: 4, mt: 0.4, borderRadius: 0.5, background: '#f1f5f9', maxWidth: 200 }}>
                            <Box sx={{ height: '100%', width: `${pct}%`, background: C.verde, borderRadius: 0.5 }} />
                          </Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ p: 1.2, textAlign: 'right', fontWeight: 600 }}>{s.cantidad}</Box>
                      <Box component="td" sx={{ p: 1.2, textAlign: 'right', fontWeight: 800, color: C.verde, fontFamily: 'monospace' }}>
                        {fmtMoney(s.total)}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ) : (
          <EmptyChart label="Sin servicios facturados" />
        )}
      </ChartPanel>
    </Box>
  );
}

// ============================================================================
// HELPERS
// ============================================================================
const chartTooltip = {
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  fontSize: 11.5,
};

function KpiCard({ icon, label, valor, subtitulo, cambio, color, sparkline, sparkKey, highlight }) {
  const tieneCambio = cambio !== undefined && cambio !== null;
  const subio = tieneCambio && cambio > 0;
  const bajo = tieneCambio && cambio < 0;

  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3,
      background: '#fff',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s',
      '&:hover': {
        borderColor: '#cbd5e1',
        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
      },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          background: `${color}15`, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 18 } })}
        </Box>
        {tieneCambio && (
          <Chip
            icon={subio ? <TrendingUpIcon sx={{ fontSize: 12, color: '#15803d !important' }} />
                : bajo ? <TrendingDownIcon sx={{ fontSize: 12, color: '#dc2626 !important' }} />
                : null}
            label={`${cambio > 0 ? '+' : ''}${cambio}%`}
            size="small"
            sx={{
              height: 22, fontSize: 11, fontWeight: 700,
              background: subio ? '#dcfce7' : bajo ? '#fee2e2' : '#f1f5f9',
              color: subio ? '#15803d' : bajo ? '#991b1b' : '#475569',
              '& .MuiChip-icon': { ml: 0.4 },
            }}
          />
        )}
      </Box>

      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#1e3a5f', lineHeight: 1.1 }}>
        {valor}
      </Typography>
      {subtitulo && (
        <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.3 }}>
          {subtitulo}
        </Typography>
      )}

      {/* Sparkline — unificada a navy */}
      {sparkline && sparkline.length > 1 && (
        <Box sx={{ mt: 1, mx: -0.5 }}>
          <ResponsiveContainer width="100%" height={32}>
            <AreaChart data={sparkline}>
              <defs>
                <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1e3a5f" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#1e3a5f" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone" dataKey={sparkKey || 'total'}
                stroke="#1e3a5f" strokeWidth={1.5}
                fill={`url(#gradient-${color})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
}

function MiniStat({ label, valor, color }) {
  return (
    <Paper elevation={0} sx={{
      p: 1.2, borderRadius: 2,
      background: '#fff', border: '1px solid #e2e8f0',
      display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Box sx={{ width: 4, height: 32, borderRadius: 1, background: color, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
          {valor}
        </Typography>
      </Box>
    </Paper>
  );
}

function ChartPanel({ title, subtitulo, children }) {
  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, background: '#fff', border: '1px solid #e2e8f0' }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{title}</Typography>
        {subtitulo && (
          <Typography sx={{ fontSize: 11.5, color: '#64748b', mt: 0.2 }}>{subtitulo}</Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function EmptyChart({ label }) {
  return (
    <Box sx={{ height: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
      <InsightsIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
      <Typography sx={{ fontSize: 13 }}>{label}</Typography>
    </Box>
  );
}

// ============================================================================
// Smart Goals — objetivos sugeridos con barras de progreso
// ============================================================================
function SmartGoalsPanel({ goals }) {
  if (!goals || goals.length === 0) return null;

  const prioColor = { alta: '#dc2626', media: '#d97706', baja: '#15803d' };
  const fmtVal = (v, unidad) => {
    if (unidad === '$') return `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
    if (unidad === '%') return `${v}%`;
    return Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 });
  };

  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: 3,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: '4px solid #f59e0b',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
        <LightbulbIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Objetivos sugeridos
        </Typography>
        <Chip label={goals.length} size="small" sx={{ height: 18, fontSize: 9.5, fontWeight: 800, background: '#1e3a5f', color: '#fff' }} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(310px, 1fr))' }, gap: 1.2 }}>
        {goals.map((g) => {
          // Calcular progreso. Si métrica invertida (no-show, AR), progreso = qué tan cerca estamos del objetivo BAJANDO
          const invertido = g.invertido;
          const actual = Number(g.actual);
          const objetivo = Number(g.objetivo);
          let progreso = 0;
          if (invertido) {
            // Mejor cuando es menor; ya lo cumplió si actual <= objetivo
            if (actual <= objetivo) progreso = 100;
            else if (actual > 0) progreso = Math.max(0, 100 - ((actual - objetivo) / actual) * 100);
          } else {
            progreso = objetivo > 0 ? Math.min((actual / objetivo) * 100, 100) : 0;
          }

          return (
            <Box key={g.id} sx={{
              p: 1.5, borderRadius: 2,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <Typography sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{g.icono}</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b' }}>
                      {g.metrica}
                    </Typography>
                    <Box sx={{
                      px: 0.6, py: 0.05, borderRadius: 0.5,
                      background: prioColor[g.prioridad] || '#64748b',
                      color: '#fff', fontSize: 9, fontWeight: 800,
                      textTransform: 'uppercase', letterSpacing: 0.3,
                    }}>
                      {g.prioridad}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.2 }}>
                    Plazo: {g.plazo_dias} días
                  </Typography>
                </Box>
              </Box>

              {/* Valor actual → objetivo */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.8 }}>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#475569' }}>
                  {fmtVal(actual, g.unidad)}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>→</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: prioColor[g.prioridad] || '#1e3a5f' }}>
                  {fmtVal(objetivo, g.unidad)}
                </Typography>
              </Box>

              {/* Progress bar */}
              <Box sx={{ height: 6, borderRadius: 0.8, background: '#f1f5f9', overflow: 'hidden', mb: 0.8 }}>
                <Box sx={{
                  height: '100%', width: `${progreso}%`,
                  background: `linear-gradient(90deg, ${prioColor[g.prioridad] || '#1e3a5f'} 0%, ${prioColor[g.prioridad] || '#2d5a87'}cc 100%)`,
                  transition: 'width 0.5s',
                }} />
              </Box>

              <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.4 }}>
                {g.razonamiento}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Forecast — chart con histórico + proyección
// ============================================================================
function ForecastChart({ data, color, unidad }) {
  if (!data || (!data.historico?.length && !data.proyeccion?.length)) {
    return <EmptyChart label="Sin datos para proyectar" />;
  }

  const hist = (data.historico || []).map(d => ({ fecha: d.fecha, historico: d.valor, proyeccion: null }));
  const proy = (data.proyeccion || []).map(d => ({ fecha: d.fecha, historico: null, proyeccion: d.valor }));
  // Punto de unión: último día histórico también marca inicio de proyección
  if (hist.length > 0) {
    proy.unshift({ fecha: hist[hist.length - 1].fecha, historico: null, proyeccion: hist[hist.length - 1].historico });
  }
  const combinada = [...hist, ...proy];

  const tendIcon = data.tendencia === 'subiendo' ? <TrendingUpIcon sx={{ fontSize: 14 }} />
    : data.tendencia === 'bajando' ? <TrendingDownIcon sx={{ fontSize: 14 }} /> : null;
  const tendColor = data.tendencia === 'subiendo' ? '#15803d' : data.tendencia === 'bajando' ? '#dc2626' : '#64748b';
  const confColor = { alta: '#15803d', media: '#d97706', baja: '#dc2626' }[data.confianza] || '#64748b';

  const fmtY = (v) => {
    if (unidad === '$') return `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
    return Math.round(v);
  };

  return (
    <Box>
      {/* Top: tendencia + cambio % + confianza */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip
          icon={tendIcon || undefined}
          label={`Tendencia ${data.tendencia}`}
          size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 700, background: `${tendColor}22`, color: tendColor, '& .MuiChip-icon': { color: `${tendColor} !important` } }}
        />
        <Chip
          label={`${data.cambio_pct > 0 ? '+' : ''}${data.cambio_pct}% próximo período`}
          size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#1e3a5f' }}
        />
        <Chip
          label={`Confianza ${data.confianza}`}
          size="small"
          sx={{ height: 22, fontSize: 11, fontWeight: 700, background: `${confColor}22`, color: confColor }}
        />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>
          Promedio histórico: <b>{fmtY(data.promedio_historico)}</b>/día
        </Typography>
      </Box>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={combinada}>
          <defs>
            <linearGradient id={`fgrad-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false}
            tickFormatter={(s) => {
              try {
                const d = new Date(s);
                return `${d.getDate()}/${d.getMonth() + 1}`;
              } catch { return s; }
            }}
            interval={Math.floor(combinada.length / 8)}
          />
          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false}
            tickFormatter={fmtY}
          />
          <RTooltip contentStyle={chartTooltip} formatter={(v) => v != null ? [fmtY(v)] : null} />
          <Area type="monotone" dataKey="historico" stroke={color} strokeWidth={2.5} fill={`url(#fgrad-${color})`} connectNulls={false} name="Histórico" />
          <Area type="monotone" dataKey="proyeccion" stroke={color} strokeWidth={2.5} strokeDasharray="6 4" fill="none" connectNulls={false} name="Proyección" />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ============================================================================
// Briefing IA — banner destacado con resumen + insights + recomendaciones
// ============================================================================
function BriefingIABanner({ data, loading, onRegenerar }) {
  if (loading && !data) {
    return (
      <Paper elevation={0} sx={{
        p: 2.5, mb: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 50%, #6366f1 100%)',
        color: '#fff', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 2 }}>
          <AutoAwesomeIcon sx={{ color: '#fbbf24', fontSize: 22 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            Briefing NARA · Generando...
          </Typography>
        </Box>
        <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.15)', height: 24, width: '85%' }} />
        <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.12)', height: 20, width: '60%' }} />
      </Paper>
    );
  }

  if (!data || !data.briefing) return null;
  const { briefing, generado_en, from_cache } = data;

  if (briefing.error) {
    return (
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, background: '#fef2f2', border: '1px solid #fca5a5' }}>
        <Typography sx={{ fontSize: 12.5, color: '#991b1b' }}>
          ⚠ No se pudo generar el briefing IA: {briefing.error}
        </Typography>
        <Button size="small" onClick={onRegenerar} sx={{ mt: 0.5, textTransform: 'none', color: '#dc2626', fontSize: 11 }}>
          Reintentar
        </Button>
      </Paper>
    );
  }

  const fechaTxt = generado_en
    ? new Date(generado_en).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';

  const tipoConfig = {
    logro:       { color: '#15803d', bg: '#dcfce7' },
    alerta:      { color: '#dc2626', bg: '#fee2e2' },
    oportunidad: { color: '#d97706', bg: '#fef3c7' },
    tendencia:   { color: '#1e3a5f', bg: '#eff6ff' },
  };
  const prioridadConfig = {
    alta:  { color: '#dc2626', label: 'Alta' },
    media: { color: '#d97706', label: 'Media' },
    baja:  { color: '#15803d', label: 'Baja' },
  };

  return (
    <Paper elevation={0} sx={{
      p: 2.5, mb: 3, borderRadius: 3,
      background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #2d5a87 100%)',
      color: '#fff', position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative pattern */}
      <Box sx={{
        position: 'absolute', top: -40, right: -40, width: 200, height: 200,
        borderRadius: '50%', background: 'radial-gradient(circle, rgba(251,191,36,0.18), transparent 70%)',
        pointerEvents: 'none',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 16px rgba(251,191,36,0.4)',
          }}>
            <AutoAwesomeIcon sx={{ color: '#1e3a5f', fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#fbbf24', letterSpacing: 0.8, textTransform: 'uppercase', lineHeight: 1 }}>
              Briefing NARA
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', mt: 0.2 }}>
              {from_cache ? '↻ ' : '⚡ '}{fechaTxt}{from_cache ? ' (en caché)' : ' (recién generado)'}
            </Typography>
          </Box>
          <Tooltip title="Regenerar análisis">
            <IconButton size="small" onClick={onRegenerar} disabled={loading}
              sx={{ color: 'rgba(255,255,255,0.85)', '&:hover': { background: 'rgba(255,255,255,0.12)' } }}>
              <RefreshIcon sx={{ fontSize: 18, animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Resumen ejecutivo */}
        {briefing.resumen && (
          <Typography sx={{
            fontSize: 14.5, lineHeight: 1.55, mb: 2,
            color: '#fff', fontWeight: 500,
          }}>
            {briefing.resumen}
          </Typography>
        )}

        {/* Insights */}
        {(briefing.insights || []).length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(260px, 1fr))' }, gap: 1.2, mb: (briefing.recomendaciones || []).length > 0 ? 2 : 0 }}>
            {briefing.insights.map((ins, i) => {
              const cfg = tipoConfig[ins.tipo] || tipoConfig.tendencia;
              return (
                <Box key={i} sx={{
                  p: 1.3, borderRadius: 2,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderLeft: `3px solid ${cfg.color}`,
                  display: 'flex', gap: 1, alignItems: 'flex-start',
                }}>
                  <Typography sx={{ fontSize: 16, lineHeight: 1, mt: 0.2, flexShrink: 0 }}>
                    {ins.icono || '•'}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', mb: 0.2 }}>
                      {ins.titulo}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
                      {ins.descripcion}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Recomendaciones */}
        {(briefing.recomendaciones || []).length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.8 }}>
              Recomendaciones
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
              {briefing.recomendaciones.map((rec, i) => {
                const pcfg = prioridadConfig[rec.prioridad] || prioridadConfig.media;
                return (
                  <Box key={i} sx={{
                    p: 1.2, borderRadius: 1.5,
                    background: 'rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'flex-start', gap: 1.2,
                  }}>
                    <Box sx={{
                      px: 0.8, py: 0.2, borderRadius: 1,
                      background: pcfg.color, color: '#fff',
                      fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
                      flexShrink: 0,
                    }}>
                      {pcfg.label}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#fff', mb: 0.1 }}>
                        {rec.titulo}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
                        {rec.descripcion}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Anomalías detectadas — banner compacto con alertas
// ============================================================================
function AnomaliasBanner({ anomalias }) {
  if (!anomalias || anomalias.length === 0) return null;

  const sev = (a) => ({ alta: 0, media: 1, info: 2 }[a.severidad] || 1);
  const orden = [...anomalias].sort((a, b) => sev(a) - sev(b));

  const tipoConfig = {
    alerta:      { color: '#991b1b', bg: '#fee2e2', border: '#fca5a5', icon: <WarningIcon /> },
    oportunidad: { color: '#92400e', bg: '#fef3c7', border: '#fde68a', icon: <LightbulbIcon /> },
    logro:       { color: '#15803d', bg: '#dcfce7', border: '#86efac', icon: <CheckIcon /> },
  };

  return (
    <Paper elevation={0} sx={{ p: 1.5, mb: 3, borderRadius: 3, background: '#fff', border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
        <WarningIcon sx={{ fontSize: 16, color: '#dc2626' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Anomalías detectadas ({orden.length})
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {orden.map((a, i) => {
          const cfg = tipoConfig[a.tipo] || tipoConfig.alerta;
          return (
            <Box key={i} sx={{
              p: 1, borderRadius: 1.5,
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderLeft: `4px solid ${cfg.color}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              {React.cloneElement(cfg.icon, { sx: { fontSize: 16, color: cfg.color, flexShrink: 0 } })}
              <Typography sx={{ fontSize: 12.5, color: cfg.color, fontWeight: 600, flex: 1 }}>
                {a.mensaje}
              </Typography>
              {a.severidad === 'alta' && (
                <Chip label="Alta" size="small" sx={{
                  height: 18, fontSize: 9.5, fontWeight: 800,
                  background: cfg.color, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.3,
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function InsightsBar({ insights }) {
  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: 3,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: '4px solid #1e3a5f',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoAwesomeIcon sx={{ color: '#1e3a5f', fontSize: 18 }} />
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Insights NARA
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(280px, 1fr))' }, gap: 1.5 }}>
        {insights.slice(0, 3).map((ins, i) => {
          const colorByTipo = {
            exito: { c: C.verde, bg: '#dcfce7', icon: <CheckIcon /> },
            alerta: { c: C.rojo, bg: '#fee2e2', icon: <WarningIcon /> },
            oportunidad: { c: C.ambar, bg: '#fef3c7', icon: <LightbulbIcon /> },
          };
          const cfg = colorByTipo[ins.tipo] || colorByTipo.oportunidad;
          return (
            <Box key={i} sx={{
              p: 1.5, borderRadius: 2, background: '#fff',
              border: `1px solid ${cfg.c}33`, borderLeft: `4px solid ${cfg.c}`,
              display: 'flex', gap: 1.2, alignItems: 'flex-start',
            }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1.5,
                background: cfg.bg, color: cfg.c,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {React.cloneElement(cfg.icon, { sx: { fontSize: 16 } })}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e293b', mb: 0.2 }}>
                  {ins.titulo}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.4 }}>
                  {ins.descripcion}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function PlaceholderTab({ icon, titulo, descripcion, tareasPendientes }) {
  return (
    <Paper elevation={0} sx={{
      p: 5, borderRadius: 3, textAlign: 'center',
      background: '#fff', border: '1px dashed #cbd5e1',
    }}>
      <Box sx={{
        width: 64, height: 64, borderRadius: '50%',
        background: '#f1f5f9', mx: 'auto', mb: 2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { sx: { fontSize: 32, color: '#94a3b8' } })}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#475569', mb: 1 }}>
        {titulo}
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#64748b', mb: 3, maxWidth: 480, mx: 'auto' }}>
        {descripcion}
      </Typography>
      <Box sx={{
        display: 'inline-block', textAlign: 'left',
        p: 2, borderRadius: 2,
        background: '#f8fafc', border: '1px solid #e2e8f0',
      }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
          Métricas planificadas (Sub-paso 2)
        </Typography>
        {tareasPendientes.map((t, i) => (
          <Typography key={i} sx={{ fontSize: 12, color: '#64748b', mb: 0.3 }}>
            • {t}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
}

function LoadingState() {
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
        {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 3 }} />)}
      </Box>
      <Skeleton variant="rounded" height={280} sx={{ borderRadius: 3 }} />
    </Box>
  );
}

function ErrorState({ mensaje, onRetry }) {
  return (
    <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px solid #fca5a5', background: '#fef2f2' }}>
      <WarningIcon sx={{ fontSize: 48, color: '#dc2626', mb: 1.5 }} />
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#991b1b', mb: 0.5 }}>
        Error al cargar analítica
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#7f1d1d', mb: 2 }}>{mensaje}</Typography>
      <Button variant="outlined" onClick={onRetry} sx={{ borderColor: '#dc2626', color: '#dc2626', textTransform: 'none' }}>
        Reintentar
      </Button>
    </Paper>
  );
}
